import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { seedLanguageFromUser } from '../hooks/useAppLanguage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Détail structuré `{error, message}` — la forme que renvoient les
  // refus de mot de passe et de consentement, sur le modèle des codes
  // promo. Sans cette ligne, `String(detail)` affichait « [object
  // Object] » à l'utilisateur.
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

/** Le code machine d'un détail structuré, ou `null`. */
function apiErrorCode(detail) {
  return (detail && typeof detail === "object" && typeof detail.error === "string")
    ? detail.error
    : null;
}

/**
 * Construit l'Error remontée aux formulaires. Le `message` est le repli
 * français du serveur ; `code` est ce que l'écran TRADUIT — un
 * formulaire trilingue ne peut pas afficher la phrase française du
 * backend à un utilisateur néerlandophone.
 */
function authError(detail) {
  const err = new Error(formatApiError(detail));
  err.code = apiErrorCode(detail);
  return err;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Un seul point d'entrée pour « l'utilisateur vient d'être identifié ».
  // Les quatre chemins d'authentification (session Google, login e-mail,
  // inscription, revalidation au chargement) appelaient chacun `setUser`
  // directement ; aucun n'était donc l'endroit évident où accrocher la
  // langue du compte. Ici, ils convergent.
  //
  // `seedLanguageFromUser` n'écrit QUE si l'appareil n'a encore aucun
  // choix de langue : quelqu'un qui vient de basculer le sélecteur du site
  // ne doit pas se le faire écraser par la valeur enregistrée à
  // l'inscription. Elle sert au cas inverse — se connecter depuis un
  // navigateur neuf, où le compte est la seule mémoire disponible.
  const adoptUser = useCallback((nextUser) => {
    setUser(nextUser);
    if (nextUser) seedLanguageFromUser(nextUser);
    return nextUser;
  }, []);

  const checkAuth = useCallback(async () => {
    // LA BRANCHE `#session_id=` A DISPARU AVEC LE COURTIER EMERGENT.
    // Elle suspendait la vérification de session tant qu'un fragment
    // `#session_id=` traînait dans l'URL, le temps qu'`AuthCallback`
    // l'échange. Le nouveau flux Google revient par une REDIRECTION du
    // serveur, cookie déjà posé : il n'y a rien à échanger côté client,
    // et l'app doit lire `/auth/me` immédiatement comme sur tout autre
    // chargement.
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      adoptUser(response.data);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [adoptUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Email/password login
  // `remember` porte la case « Se souvenir de moi » de l'écran de
  // connexion. Le serveur en tire la durée de la session (7 jours
  // décoché — le comportement historique —, 30 jours coché) et la
  // persistance du cookie. Défaut `false` : une session longue est un
  // choix, jamais un défaut.
  const loginWithEmail = async (email, password, remember = false) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/login`,
        { email, password, remember: !!remember },
        { withCredentials: true }
      );
      return adoptUser(response.data.user);
    } catch (err) {
      const e = authError(err.response?.data?.detail);
      setError(e.message);
      throw e;
    }
  };

  // ── ADOPTER UNE SESSION QU'ON VIENT D'OUVRIR ───────────────────────
  //
  // LE DÉFAUT QU'ELLE FERME. Le dépôt sans inscription ouvrait une vraie
  // session côté serveur (`/intake/start`) et posait son cookie — mais
  // ne le disait JAMAIS à ce contexte. `user` restait à `null`, celui du
  // visiteur anonyme au démarrage, et la navigation suivante traversait
  // `ProtectedRoute` : `if (!user) → <Navigate to="/login">`. Le
  // visiteur était donc jeté sur l'écran de connexion à la seconde où il
  // cliquait « Analyser mon document », sans jamais voir sa cinématique
  // ni son verdict. Le parcours anonyme entier mourait à sa première
  // navigation.
  //
  // ELLE NE FAIT PAS QUE POSER `user`, ELLE PROUVE LA SESSION. Poser le
  // profil rendu par `/intake/start` sans rien vérifier ferait passer
  // `ProtectedRoute` et laisserait la cinématique sonder `/cases/{id}`
  // avec un cookie qui n'a peut-être pas pris — écran monté, données en
  // 401, et personne pour le dire. Le `GET /auth/me` fait les deux d'un
  // coup : il prouve que le cookie voyage, et il rend le profil.
  //
  // LE FILET. Si le cookie n'a PAS pris (Safari sur un `SameSite=None`
  // tiers, montage à deux origines), on tient encore le jeton porteur :
  // `/auth/session/cookie` le présente et fait re-poser le cookie. On ne
  // fabrique aucune session — le serveur refuse tout appelant qui n'en
  // détient pas déjà une.
  const adoptSession = useCallback(async (sessionToken) => {
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      return adoptUser(me.data);
    } catch (err) {
      if (!sessionToken || err?.response?.status !== 401) throw err;
    }
    const res = await axios.post(`${API}/auth/session/cookie`, null, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
    return adoptUser(res.data?.user || null);
  }, [adoptUser]);

  // ── LA CONNEXION PAR CODE ──────────────────────────────────────────
  //
  // Elle n'est pas un confort : un compte né du DÉPÔT SANS INSCRIPTION
  // n'a pas de mot de passe, et `/auth/login` le refuse explicitement.
  // Sans ces deux fonctions, un tel compte serait enfermé dehors au
  // premier retour.
  //
  // `requestLoginCode` ne dit JAMAIS si l'adresse existe — le serveur
  // répond la même chose dans les deux cas, pour ne pas devenir un
  // oracle où l'on teste qui est client d'un service juridique. Le front
  // ne peut donc rien afficher d'autre que « si un compte existe, le
  // code est parti », et c'est voulu.
  const requestLoginCode = async (email) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/code/request`,
        { email }, { withCredentials: true });
      return response.data;
    } catch (err) {
      const e = authError(err.response?.data?.detail);
      setError(e.message);
      throw e;
    }
  };

  const loginWithCode = async (email, code, remember = false) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/code/verify`,
        { email, code, remember: !!remember }, { withCredentials: true });
      return adoptUser(response.data.user);
    } catch (err) {
      const e = authError(err.response?.data?.detail);
      setError(e.message);
      throw e;
    }
  };

  // Email/password register. `extras` carries the optional insurance-flow
  // profile fields (DOB, address, phone, national register number) — these
  // are required to email the insurer when the plan is starter/pro.
  const registerWithEmail = async (name, email, password, plan = 'free', jurisdiction = 'US', region = null, language = 'en', extras = {}) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/register`,
        { name, email, password, plan, jurisdiction, country: jurisdiction, region, language, ...extras },
        { withCredentials: true }
      );
      return adoptUser(response.data.user);
    } catch (err) {
      const e = authError(err.response?.data?.detail);
      setError(e.message);
      throw e;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  /**
   * « Continuer avec Google » — une VRAIE connexion depuis le 19/08/2026.
   *
   * Le bouton envoyait sur `auth.emergentagent.com` (le courtier de la
   * plateforme de départ), qui revenait avec `#session_id=…`, échangé par
   * `POST /api/auth/session` auprès de `demobackend.emergentagent.com`.
   * Ce point d'échange rend 404 depuis le déménagement : le flux ne
   * pouvait plus aboutir. Il avait donc été neutralisé en
   * `window.location.href = '/login'` — le bouton rechargeait l'écran de
   * connexion, ce qui est exactement « ça ne marche pas ».
   *
   * Ce qui le remplace tient en une ligne côté client : on SORT de l'app
   * vers une route du serveur, qui redirige vers Google, reçoit le
   * retour, ouvre la session et repose le client sur `next`. Aucun jeton
   * ne transite par le navigateur, et il n'y a plus rien à échanger ici.
   *
   * `remember` VOYAGE AVEC. C'est la même case que le formulaire, donc
   * la même règle de session : cochée, 30 jours et cookie persistant ;
   * décochée, cookie de session. Une connexion sociale qui ignorerait la
   * case ferait mentir la case.
   */
  const initiateGoogleLogin = (remember = false, next = '/dashboard') => {
    const params = new URLSearchParams({
      remember: remember ? '1' : '0',
      next: next || '/dashboard',
    });
    window.location.href = `${API}/auth/google/start?${params.toString()}`;
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      loginWithEmail,
      adoptSession,
      requestLoginCode,
      loginWithCode,
      registerWithEmail,
      logout,
      updateUser,
      checkAuth,
      refreshUser: checkAuth,
      initiateGoogleLogin,
      clearError,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
